import type { PiiType, TextDetection } from './types';

type Recognizer = {
  type: PiiType;
  pattern: RegExp;
  /** Optional post-validation; reject the match if it returns false. */
  validate?: (match: string) => boolean;
  /** Optional confidence override based on the match content. */
  confidence: number | ((match: string) => number);
};

/**
 * Luhn check for credit-card validation. Strips non-digits before checking.
 * Length range covers Amex (15) through Visa/MC/Discover (16) — narrower
 * than the historical 13-19 to keep partial transcriptions out.
 */
export const luhn = (raw: string): boolean => {
  const digits = raw.replace(/\D/g, '');
  if (digits.length < 15 || digits.length > 16) return false;
  let sum = 0;
  let alt = false;
  for (let i = digits.length - 1; i >= 0; i--) {
    const ch = digits[i];
    if (ch === undefined) return false;
    let n = ch.charCodeAt(0) - 48;
    if (n < 0 || n > 9) return false;
    if (alt) {
      n *= 2;
      if (n > 9) n -= 9;
    }
    sum += n;
    alt = !alt;
  }
  return sum % 10 === 0;
};

const isLikelyPhone = (s: string): boolean => {
  const digits = s.replace(/\D/g, '');
  if (digits.length < 10 || digits.length > 15) return false;
  if (/^(\d)\1+$/.test(digits)) return false;
  return true;
};

/** Luhn check for arbitrary digit length. Used for 9-digit SINs (the
 *  exported `luhn` enforces 13-19 for credit cards). */
const luhnExact = (digits: string): boolean => {
  let sum = 0;
  let alt = false;
  for (let i = digits.length - 1; i >= 0; i--) {
    const n = digits.charCodeAt(i) - 48;
    if (n < 0 || n > 9) return false;
    let d = n;
    if (alt) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
    alt = !alt;
  }
  return sum % 10 === 0;
};

/** ISO 13616 mod-97 check. Move the first 4 chars to the end, replace
 *  letters with digits (A=10..Z=35), and verify the resulting integer
 *  is congruent to 1 mod 97. */
const isValidIban = (s: string): boolean => {
  const clean = s.replace(/\s/g, '').toUpperCase();
  if (clean.length < 15 || clean.length > 34) return false;
  const rearranged = clean.slice(4) + clean.slice(0, 4);
  let remainder = 0;
  for (const ch of rearranged) {
    const code = ch.charCodeAt(0);
    let digits: string;
    if (code >= 48 && code <= 57) digits = ch;
    else if (code >= 65 && code <= 90) digits = String(code - 55);
    else return false;
    for (const d of digits) {
      remainder = (remainder * 10 + (d.charCodeAt(0) - 48)) % 97;
    }
  }
  return remainder === 1;
};

const isCanadianSin = (s: string): boolean => {
  const d = s.replace(/\D/g, '');
  if (d.length !== 9) return false;
  // Real SINs start with 1-7 or 9 (8 unassigned, 0 reserved).
  if (d.startsWith('0') || d.startsWith('8')) return false;
  return luhnExact(d);
};

/** ABA routing number check digit. 9 digits, weighted 3-7-1-3-7-1-3-7-1
 *  must sum to a multiple of 10. Tight enough that random 9-digit
 *  sequences almost never pass — well below 10 % rate empirically. */
const isAbaRouting = (s: string): boolean => {
  const d = s.replace(/\D/g, '');
  if (d.length !== 9) return false;
  const w = [3, 7, 1, 3, 7, 1, 3, 7, 1];
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    const n = (d.charCodeAt(i) - 48) * (w[i] ?? 0);
    sum += n;
  }
  return sum % 10 === 0;
};

/** VIN check: 17 chars, no I/O/Q, position-9 weighted check digit per
 *  ISO 3779 / FMVSS 565. Letter transliteration table is fixed. */
const VIN_LETTER_VALUE: Record<string, number> = {
  A: 1, B: 2, C: 3, D: 4, E: 5, F: 6, G: 7, H: 8,
  J: 1, K: 2, L: 3, M: 4, N: 5, P: 7, R: 9,
  S: 2, T: 3, U: 4, V: 5, W: 6, X: 7, Y: 8, Z: 9,
};
const VIN_WEIGHTS = [8, 7, 6, 5, 4, 3, 2, 10, 0, 9, 8, 7, 6, 5, 4, 3, 2];
const isValidVin = (raw: string): boolean => {
  const s = raw.toUpperCase();
  if (s.length !== 17) return false;
  if (/[IOQ]/.test(s)) return false;
  let sum = 0;
  for (let i = 0; i < 17; i++) {
    const ch = s[i] ?? '';
    let v: number;
    if (ch >= '0' && ch <= '9') v = ch.charCodeAt(0) - 48;
    else {
      const lookup = VIN_LETTER_VALUE[ch];
      if (lookup === undefined) return false;
      v = lookup;
    }
    sum += v * (VIN_WEIGHTS[i] ?? 0);
  }
  const checkVal = sum % 11;
  const expected = checkVal === 10 ? 'X' : String(checkVal);
  return s[8] === expected;
};

/** Verhoeff checksum (Aadhaar). Tables baked in. The Aadhaar number's
 *  last digit is a check digit computed from the leading 11. Running the
 *  full 12 digits through Verhoeff right-to-left must yield 0. Catches
 *  all single-digit substitutions and ~90 % of random sequences — drops
 *  the false-positive rate on bare 12-digit account numbers to near
 *  zero. */
const VERHOEFF_D: readonly (readonly number[])[] = [
  [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
  [1, 2, 3, 4, 0, 6, 7, 8, 9, 5],
  [2, 3, 4, 0, 1, 7, 8, 9, 5, 6],
  [3, 4, 0, 1, 2, 8, 9, 5, 6, 7],
  [4, 0, 1, 2, 3, 9, 5, 6, 7, 8],
  [5, 9, 8, 7, 6, 0, 4, 3, 2, 1],
  [6, 5, 9, 8, 7, 1, 0, 4, 3, 2],
  [7, 6, 5, 9, 8, 2, 1, 0, 4, 3],
  [8, 7, 6, 5, 9, 3, 2, 1, 0, 4],
  [9, 8, 7, 6, 5, 4, 3, 2, 1, 0],
];
const VERHOEFF_P: readonly (readonly number[])[] = [
  [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
  [1, 5, 7, 6, 2, 8, 3, 0, 9, 4],
  [5, 8, 0, 3, 7, 9, 6, 1, 4, 2],
  [8, 9, 1, 6, 0, 4, 3, 5, 2, 7],
  [9, 4, 5, 3, 1, 2, 6, 8, 7, 0],
  [4, 2, 8, 6, 5, 7, 3, 9, 0, 1],
  [2, 7, 9, 3, 8, 0, 6, 4, 1, 5],
  [7, 0, 4, 6, 9, 1, 3, 2, 5, 8],
];
const isValidAadhaar = (raw: string): boolean => {
  const digits = raw.replace(/\D/g, '');
  if (digits.length !== 12) return false;
  // Real Aadhaar numbers start 2-9 per UIDAI spec.
  const first = digits.charCodeAt(0) - 48;
  if (first < 2 || first > 9) return false;
  let c = 0;
  for (let i = 0; i < digits.length; i++) {
    const digit = digits.charCodeAt(digits.length - 1 - i) - 48;
    if (digit < 0 || digit > 9) return false;
    const pRow = VERHOEFF_P[i % 8];
    const dRow = VERHOEFF_D[c];
    if (!pRow || !dRow) return false;
    const p = pRow[digit];
    if (p === undefined) return false;
    const next = dRow[p];
    if (next === undefined) return false;
    c = next;
  }
  return c === 0;
};

const RECOGNIZERS: Recognizer[] = [
  {
    type: 'email',
    pattern: /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi,
    confidence: 0.95,
  },
  // US Employer Identification Number (EIN) — XX-XXXXXXX. The IRS
  // assigns these to businesses on lease + tax + payroll docs. The
  // dashed form is distinctive enough that we don't need a label
  // anchor; bare 7-digit groups after the prefix dash almost never
  // appear in non-EIN text.
  {
    type: 'ein',
    pattern: /\b\d{2}-\d{7}\b/g,
    confidence: 0.9,
  },
  {
    // Any XXX-XX-XXXX. The earlier version rejected 9XX-area numbers
    // (technically not valid SSNs) but those collide with IRS Individual
    // Taxpayer Identification Numbers — which ARE issued and ARE PII.
    // It also rejected the 000 / 666 / 00-group / 0000-serial sentinels,
    // but those frequently appear as test/placeholder values in real
    // documents and users still want them redacted before they paste
    // the doc somewhere. Match the shape; trust the user's intent.
    type: 'ssn',
    pattern: /\b\d{3}-\d{2}-\d{4}\b/g,
    confidence: 0.9,
  },
  {
    type: 'credit_card',
    pattern: /\b(?:\d[ -]?){14,15}\d\b/g,
    validate: luhn,
    confidence: 0.95,
  },
  {
    type: 'iban',
    pattern: /\b[A-Z]{2}\d{2}[A-Z0-9]{11,30}\b/g,
    validate: isValidIban,
    confidence: 0.95,
  },
  {
    type: 'phone',
    pattern: /\+\d[\d \-().]{6,18}\d/g,
    validate: isLikelyPhone,
    confidence: 0.85,
  },
  {
    type: 'phone',
    pattern: /(?:\+?1[\s.-]?)?\(?[2-9]\d{2}\)?[\s.-]?[2-9]\d{2}[\s.-]?\d{4}\b/g,
    validate: isLikelyPhone,
    confidence: 0.85,
  },
  // US ZIP. Bare 5-digit numbers are too noisy on their own (every
  // invoice/order/document ID has them). We match only when the shape
  // is unambiguous: a full ZIP+4 (12345-6789), a 5-digit number
  // immediately preceded by a state/territory abbreviation, or one
  // that follows a parenthesised/bracketed "(Zip)" form-field label
  // (common on OCR'd US forms even mid-line). The form-field
  // detector picks up labelled cases like "Zip: 10001" with a colon.
  {
    type: 'us_zip',
    pattern: /\b\d{5}-\d{4}\b/g,
    confidence: 0.95,
  },
  {
    type: 'us_zip',
    pattern:
      /\b(?:A[LKZRS]|C[AOT]|D[CE]|FL|G[AU]|HI|I[DLNA]|K[SY]|LA|M[ADEINOSTP]|N[CDEHJMVY]|O[HKR]|P[AR]|RI|S[CD]|T[NX]|UT|V[ATI]|W[AIVY])\s+\d{5}(?:-\d{4})?\b/g,
    confidence: 0.9,
  },
  {
    // Full state name (spelled out) followed by a 5-digit ZIP. Catches
    // address lines like "Fraser, Michigan 48026" that the 2-letter
    // pattern above misses.
    type: 'us_zip',
    pattern:
      /\b(?:Alabama|Alaska|Arizona|Arkansas|California|Colorado|Connecticut|Delaware|Florida|Georgia|Hawaii|Idaho|Illinois|Indiana|Iowa|Kansas|Kentucky|Louisiana|Maine|Maryland|Massachusetts|Michigan|Minnesota|Mississippi|Missouri|Montana|Nebraska|Nevada|New Hampshire|New Jersey|New Mexico|New York|North Carolina|North Dakota|Ohio|Oklahoma|Oregon|Pennsylvania|Rhode Island|South Carolina|South Dakota|Tennessee|Texas|Utah|Vermont|Virginia|Washington|West Virginia|Wisconsin|Wyoming|District of Columbia|Puerto Rico)\s+\d{5}(?:-\d{4})?\b/g,
    confidence: 0.9,
  },
  {
    // (Zip) / [Zip] / (Zipcode) / (Postal) / (Postal Code) followed
    // within ~30 chars by 5 or 9 digits. Mid-line OK — the brackets
    // are a strong enough boundary that "(zip) is 02128" doesn't
    // false-match (the "is" between bracket and digits has letters,
    // which the separator class disallows). Separator is anything
    // that isn't a letter or digit, which covers all the ways
    // Tesseract renders form-field underscore-leaders (`____`,
    // `- - -`, `· · ·`, plain whitespace, etc.).
    type: 'us_zip',
    pattern:
      /[([](?:zip(?:\s?code)?|zipcode|postal(?:\s?code)?|postcode)[)\]][^A-Za-z0-9]{0,30}\d{5}(?:-\d{4})?\b/gi,
    confidence: 0.9,
  },
  {
    type: 'ipv4',
    pattern: /\b(?:(?:25[0-5]|2[0-4]\d|[01]?\d\d?)\.){3}(?:25[0-5]|2[0-4]\d|[01]?\d\d?)\b/g,
    confidence: 0.9,
  },
  {
    type: 'ipv6',
    pattern:
      /\b(?:[A-F0-9]{1,4}:){7}[A-F0-9]{1,4}\b|\b(?:[A-F0-9]{1,4}:){1,7}:(?:[A-F0-9]{1,4}:){0,6}[A-F0-9]{1,4}\b/gi,
    confidence: 0.85,
  },
  {
    type: 'api_key',
    pattern:
      /\b(?:sk-(?:proj-|live-|test-)?[A-Za-z0-9_-]{20,}|ghp_[A-Za-z0-9]{36}|gho_[A-Za-z0-9]{36}|github_pat_[A-Za-z0-9_]{20,}|AKIA[0-9A-Z]{16}|xox[abprs]-[A-Za-z0-9-]{10,})\b/g,
    confidence: 0.95,
  },

  // Dates — three common formats: numeric (01/15/2026, 1-15-26), ISO
  // (2026-01-15), and word (January 15, 2026 / 15 Jan 2026).
  {
    type: 'date',
    pattern: /\b(?:0?[1-9]|1[0-2])[/-](?:0?[1-9]|[12]\d|3[01])[/-](?:\d{2}|\d{4})\b/g,
    confidence: 0.85,
  },
  {
    type: 'date',
    pattern: /\b\d{4}-(?:0[1-9]|1[0-2])-(?:0[1-9]|[12]\d|3[01])\b/g,
    confidence: 0.9,
  },
  {
    type: 'date',
    pattern:
      /\b(?:Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:t(?:ember)?)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)\.?\s+\d{1,2}(?:st|nd|rd|th)?,?\s+\d{4}\b/gi,
    confidence: 0.9,
  },
  {
    type: 'date',
    pattern:
      /\b\d{1,2}(?:st|nd|rd|th)?\s+(?:Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:t(?:ember)?)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)\.?,?\s+\d{4}\b/gi,
    confidence: 0.9,
  },

  // US street address — house number + 1-4 words + a road suffix, with an
  // optional unit specifier, and an optional trailing ", City, ST ZIP"
  // tail. Catches "412 Oak Street", "456 N Main St Apt 3B", and
  // "412 Oak Street, Bethesda, MD 20814" — keeping the city/state/zip
  // inside the same detection so NER doesn't have to fill in the gap.
  {
    type: 'address',
    pattern:
      /\b\d{1,6}\s+(?:[NSEW]\.?\s+)?(?:[A-Z][a-zA-Z'.-]*\s+){1,4}(?:Street|St|Avenue|Ave|Road|Rd|Lane|Ln|Drive|Dr|Boulevard|Blvd|Court|Ct|Place|Pl|Plaza|Plz|Highway|Hwy|Parkway|Pkwy|Way|Terrace|Ter|Circle|Cir|Square|Sq)\b\.?(?:\s+(?:NE|NW|SE|SW|N|S|E|W))?(?:,?\s+(?:Apt|Apartment|Suite|Ste|Unit|#)\s*[A-Za-z0-9-]+)?(?:,\s+[A-Z][a-zA-Z.'-]*(?:\s+[A-Z][a-zA-Z.'-]*){0,3}(?:,\s+(?:A[LKZRS]|C[AOT]|D[CE]|FL|G[AU]|HI|I[DLNA]|K[SY]|LA|M[ADEINOSTP]|N[CDEHJMVY]|O[HKR]|P[AR]|RI|S[CD]|T[NX]|UT|V[ATI]|W[AIVY])\s+\d{5}(?:-\d{4})?)?)?/g,
    confidence: 0.85,
  },

  // International identifiers (Phase 4). All return high confidence
  // because the formats are distinctive enough to avoid false
  // positives without context.

  // UK postcode — e.g. "SW1A 1AA", "M1 1AE", "B33 8TH". Two halves
  // separated by a space; outward code is 1-2 letters + 1-2 digits +
  // optional letter, inward is digit + 2 letters.
  {
    type: 'uk_postcode',
    pattern: /\b[A-Z]{1,2}\d[A-Z\d]?\s+\d[A-Z]{2}\b/gi,
    confidence: 0.9,
  },

  // Canadian SIN — 9 digits, conventionally written 123-456-789 or
  // 123 456 789. Validate with Luhn (Canadian SINs satisfy Luhn) to
  // keep us from flagging arbitrary 9-digit sequences. Reject the
  // 0/8 prefixes that aren't issued so test values like 000-000-000
  // don't trigger.
  {
    type: 'sin',
    pattern: /\b\d{3}[-\s]\d{3}[-\s]\d{3}\b/g,
    validate: isCanadianSin,
    confidence: 0.9,
  },

  // Indian Aadhaar — 12 digits in a 4-4-4 grouping. Validated by the
  // Verhoeff checksum so random 12-digit account numbers in the same
  // shape ("7849-2231-0064" on a US lease) don't trigger.
  {
    type: 'aadhaar',
    pattern: /\b[2-9]\d{3}[\s-]\d{4}[\s-]\d{4}\b/g,
    validate: isValidAadhaar,
    confidence: 0.95,
  },

  // Bank account number — hyphen or space grouped digits. Common shapes:
  // 4-4-4 (12 digits, like the user's lease), 4-4-4-4 (16 digits), and
  // 4-3-4 (11). Confidence is intentionally lower than Aadhaar so a real
  // Verhoeff-valid Aadhaar wins the same-range overlap. Form-field
  // labelled cases ("Account Number: …") still resolve via the forms
  // detector at higher confidence; this is the fallback for table
  // layouts where the column separator didn't come through.
  {
    type: 'bank_account',
    pattern: /\b\d{4}[-\s]\d{4}[-\s]\d{4}(?:[-\s]\d{4})?\b/g,
    confidence: 0.75,
  },

  // Brazilian CPF — XXX.XXX.XXX-XX. The dotted-and-dashed form is
  // distinctive enough that we don't need to verify the check digits.
  {
    type: 'cpf',
    pattern: /\b\d{3}\.\d{3}\.\d{3}-\d{2}\b/g,
    confidence: 0.95,
  },

  // Brazilian CNPJ — XX.XXX.XXX/XXXX-XX (corporate tax ID).
  {
    type: 'cnpj',
    pattern: /\b\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}\b/g,
    confidence: 0.95,
  },

  // VIN — 17 alphanumeric chars, no I/O/Q (ISO 3779). The shape alone is
  // distinctive enough that false positives are rare; we don't enforce
  // the position-9 check digit because plenty of real-world VINs in
  // test docs, fillable forms, and reseller listings have a tweaked or
  // mis-transcribed check digit, and missing those is worse than the
  // occasional false-positive on a long unrelated alphanumeric ID.
  // Confidence is boosted when the check digit DOES validate.
  {
    type: 'vin',
    pattern: /\b[A-HJ-NPR-Z0-9]{17}\b/g,
    confidence: (m) => (isValidVin(m) ? 0.98 : 0.85),
  },

  // ABA routing number — 9 digits passing the 3-7-1 weighted checksum
  // AND preceded by a routing / ABA / RTN label within ~40 chars. The
  // bare-9-digit form was too noisy: ~10% of random 9-digit strings
  // (order IDs, tracking numbers, etc.) pass the checksum by
  // coincidence. The labelled form is reliable. The lookbehind anchors
  // the match to the digits only so redaction covers just the number.
  {
    type: 'routing_number',
    pattern:
      /(?<=\b(?:routing(?:\s*(?:number|no\.?|#))?|ABA(?:\s*(?:number|no\.?|#|routing))?|RTN)[^A-Za-z0-9\n]{0,40})\d{9}\b/gi,
    validate: isAbaRouting,
    confidence: 0.95,
  },

  // US license plate. Two safe shapes:
  //  - hyphen-grouped with a state prefix: "MD-7XK-4421", "CA-7ABC-123"
  //  - alphanumeric plate following a "plate" / "license plate" / "tag"
  //    label within ~20 chars. The label anchor keeps the looser shape
  //    from matching arbitrary product codes / serials. The plate body
  //    is 4-8 alphanumeric chars with at least one digit, optionally
  //    split by a single space or hyphen.
  {
    type: 'license_plate',
    pattern: /\b[A-Z]{2}-[A-Z0-9]{2,4}-[A-Z0-9]{3,4}\b/g,
    confidence: 0.9,
  },
  {
    type: 'license_plate',
    pattern:
      /(?<=\b(?:license\s*plate|plate(?:\s*(?:number|no\.?|#))?|tag(?:\s*(?:number|no\.?|#))?)[^A-Za-z0-9\n]{0,20})[A-Z0-9]{2,4}[-\s]?[A-Z0-9]{2,4}\b/gi,
    validate: (m) => /\d/.test(m) && /[A-Z]/i.test(m),
    confidence: 0.85,
  },

  // Driver's license / passport with an explicit single-letter prefix
  // ("L-449-872-194-038", "P-558217493"). The prefix + hyphen + digit
  // shape almost never appears in non-ID text, so a tight regex is
  // safe here even without a label nearby. The form-field detector
  // covers the labelled bare-digit cases.
  {
    type: 'drivers_license',
    pattern: /\b[DL]-\d{3}-\d{3}-\d{3}-\d{3}\b/g,
    confidence: 0.9,
  },
  {
    type: 'passport',
    pattern: /\bP-\d{7,9}\b/g,
    confidence: 0.9,
  },

  // Passport MRZ (Machine Readable Zone) — the bottom two/three lines on
  // every modern passport / many national ID cards. Contains the
  // passport number, country, name, DOB, sex, expiry. Made up of the
  // alphabet [A-Z0-9<] with `<` as a filler/separator. The `<<` pair is
  // essentially never in legitimate text, so requiring it gives a tight
  // false-positive bound. TD3 (passport) is 44 chars × 2, TD2 is 36 ×
  // 2, TD1 is 30 × 3 — accept anything in the 28+ range.
  {
    type: 'passport',
    pattern: /[A-Z0-9<]{28,}/g,
    validate: (m) => m.includes('<<'),
    confidence: 0.95,
  },
];

const resolveConfidence = (r: Recognizer, match: string): number =>
  typeof r.confidence === 'function' ? r.confidence(match) : r.confidence;

const runOne = (r: Recognizer, text: string): TextDetection[] => {
  const out: TextDetection[] = [];
  const re = new RegExp(r.pattern.source, r.pattern.flags);
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const matched = m[0];
    if (matched.length === 0) {
      re.lastIndex += 1;
      continue;
    }
    if (r.validate && !r.validate(matched)) continue;
    out.push({
      type: r.type,
      text: matched,
      start: m.index,
      end: m.index + matched.length,
      confidence: resolveConfidence(r, matched),
      source: 'regex',
    });
  }
  return out;
};

/**
 * Run all built-in recognizers and return raw detections.
 * Result may contain overlaps; call mergeDetections to dedupe.
 */
export const detectWithRegex = (text: string): TextDetection[] => {
  const all: TextDetection[] = [];
  for (const r of RECOGNIZERS) all.push(...runOne(r, text));
  return all;
};
